import { CommunityRaffle, RafflePrizeType, RaffleStatus } from '@/types/raffle';

export const formatUsd = (value: number) =>
    `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export const PRIZE_TYPE_LABELS: Record<RafflePrizeType, string> = {
    points: 'Kick Points',
    usd: 'Stake Tip',
};

export const formatDate = (iso: string) =>
    new Date(iso).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    });

/** Remaining time split into tiles — days/hours/minutes/seconds, zero-padded. */
export const countdownParts = (ms: number) => {
    const s = Math.max(0, Math.floor(ms / 1000));
    const pad = (n: number) => String(n).padStart(2, '0');
    return [
        { label: 'Days', value: pad(Math.floor(s / 86400)) },
        { label: 'Hours', value: pad(Math.floor((s % 86400) / 3600)) },
        { label: 'Min', value: pad(Math.floor((s % 3600) / 60)) },
        { label: 'Sec', value: pad(s % 60) },
    ];
};

/** "2d 04h 13m" / "04h 13m 09s" — coarse when far away, to the second when close. */
export const formatCountdown = (ms: number) => {
    const [d, h, m, s] = countdownParts(ms).map(p => p.value);
    return Number(d) > 0 ? `${Number(d)}d ${h}h ${m}m` : `${h}h ${m}m ${s}s`;
};

/** How far through its entry window a raffle is, 0–100. */
export const progressPercent = (raffle: CommunityRaffle, now: number) => {
    const start = new Date(raffle.starts_at).getTime();
    const end = new Date(raffle.ends_at).getTime();
    if (end <= start) return 100;
    return Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100));
};

/** The timestamp the visible countdown runs to: opening for upcoming, the draw otherwise. */
export const countdownTarget = (raffle: CommunityRaffle) =>
    new Date(raffle.status === 'upcoming' ? raffle.starts_at : raffle.ends_at).getTime();

export const winChance = (raffle: CommunityRaffle) => {
    const mine = raffle.my_tickets ?? 0;
    if (!mine || !raffle.total_tickets) return null;
    const pct = (mine / raffle.total_tickets) * 100;
    return pct < 0.01 ? '<0.01%' : `${pct.toFixed(pct >= 10 ? 0 : 2)}%`;
};

export const STATUS_STYLES: Record<RaffleStatus, { label: string; className: string; dot?: string }> = {
    upcoming: { label: 'Upcoming', className: 'bg-sky-500/10 text-sky-300' },
    active: { label: 'Live', className: 'bg-green-500/10 text-green-400', dot: 'bg-green-400' },
    ended: { label: 'Drawing', className: 'bg-amber-500/10 text-amber-300', dot: 'bg-amber-400' },
    finished: { label: 'Ended', className: 'bg-gray-500/10 text-gray-400' },
    cancelled: { label: 'Cancelled', className: 'bg-red-500/10 text-red-400' },
};
